import { BEIGE_600, BLUE_600, GRAY_1000, GRAY_400, GRAY_800, GRAY_900, InterVariableFontFamily, WHITE } from 'constants/styling/theme'
import { Box, IconButton, Stack } from '@mui/material'
import React, { useCallback, useLayoutEffect, useState } from 'react'

import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined'
import DoneOutlinedIcon from '@mui/icons-material/DoneOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { FeatureFlag, useFeatureFlag } from 'utils/featureFlags'
import { KeyboardEventKey } from 'constants/misc'
import Typography from '@mui/material/Typography'
import { invalidateListVisuals } from 'dataQueries/visual.query'
import { useClientGallery } from 'components/pages/Gallery/_main/ClientGallery/_main'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth0 } from 'utils/auth'
import { useRenameVisual } from 'dataQueries'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

interface Props {
  /** Label of the image badge */
  label?: string
  /** Filename of the image */
  filename?: string
}

/**
 * @component Editable filename component
 *
 * @example
 * <EditableFilename />
 */
export const EditableFilename: React.FC<Props> = ({
  label,
  filename
}) => {
  const { t } = useTranslation(['gallery'])
  const { assignmentId } = useClientGallery()
  const { roles } = useAuth0()
  const { userPropertyWorkspaceId } = useUserData()
  const { spawnSuccessToast, spawnErrorToast } = useSnackbar()
  const allowUpdateUploadedFilename = useFeatureFlag(FeatureFlag.ALLOW_UPDATE_UPLOAD_FILE_NAME)

  const queryClient = useQueryClient()
  const renameVisual = useRenameVisual()

  const [isEditFilenameMode, setIsEditFilenameMode] = useState(false)
  const [newFilename, setNewFilename] = useState(label || filename || '')

  const onRenameFilename = useCallback(() => {
    if (!newFilename || newFilename === label) return

    renameVisual.mutate(
      {
        originalFilename: newFilename,
        filename: filename || '',
        assignmentId: assignmentId.toString(),
        workspaceId: userPropertyWorkspaceId,
        isClient: roles.isClient,
      },
      {
        onSuccess: () => {
          setIsEditFilenameMode(false)
          spawnSuccessToast(t('rename_visual.success'))
          invalidateListVisuals(queryClient, assignmentId)
        },
        onError: () => {
          spawnErrorToast(t('rename_visual.error'))
        }
      }
    )
  }, [newFilename, label, renameVisual, filename, assignmentId, userPropertyWorkspaceId, roles.isClient, spawnSuccessToast, t, queryClient, spawnErrorToast])

  const onCloseEditMode = useCallback(() => {
    setNewFilename(label || '')
    setIsEditFilenameMode(false)
  }, [label])

  const keyDownHandler = useCallback((e: KeyboardEvent) => {
    switch (e.key) {
      case KeyboardEventKey.ESCAPE:
        return onCloseEditMode()
      case KeyboardEventKey.ENTER:
        return onRenameFilename()
    }
  }, [onCloseEditMode, onRenameFilename])

  useLayoutEffect(() => {
    document.addEventListener('keydown', keyDownHandler)

    return () => {
      document.removeEventListener('keydown', keyDownHandler)
    }
  }, [keyDownHandler])

  return (
    <Stack
      minWidth={0}
      flex="1 1 auto"
      flexDirection="row"
      justifyContent="flex-start"
      alignItems="center"
      gap=".8rem"
      sx={{
        ...(!isEditFilenameMode && allowUpdateUploadedFilename && {
          ':hover': {
            borderBottom: `1px solid ${GRAY_400}`,
            '.edit-icon': { display: 'flex' }
          }
        })
      }}
      onClick={() => {
        !isEditFilenameMode && allowUpdateUploadedFilename && setIsEditFilenameMode(true)
      }}
    >

      {isEditFilenameMode ?
        <Box
          flex="1 1 auto"
          minWidth={0}
          boxSizing="border-box"
          sx={{
            display: 'flex',
            alignItems: 'center',
            borderBottom: `1px solid ${BLUE_600}`,
          }}
        >

          <input
            type="text"
            value={newFilename}
            onChange={(e) => setNewFilename(e.target.value)}
            placeholder={t('rename_visual.placeholder')}
            autoFocus
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              fontSize: '1.4rem',
              fontWeight: 450,
              color: GRAY_900,
              backgroundColor: 'transparent',
              height: '2rem',
              padding: 0,
              boxShadow: 'none',
              minWidth: 0,
              fontFamily: InterVariableFontFamily
            }}
          />

          <Stack
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            gap=".6rem"
            marginLeft=".6rem"
          >

            <IconButton
              size="small"
              onClick={onCloseEditMode}
              sx={{
                border: `.76px solid ${BEIGE_600}`,
                width: '1.6rem',
                height: '1.6rem',
                color: GRAY_800,
                backgroundColor: WHITE,
                '&:hover': { backgroundColor: WHITE },
              }}
            >
              <ClearOutlinedIcon fontSize="small" />
            </IconButton>

            <IconButton
              size="small"
              sx={{
                width: '1.6rem',
                height: '1.6rem',
                color: WHITE,
                backgroundColor: GRAY_1000,
                '&:hover': { backgroundColor: GRAY_1000 },
              }}
              onClick={onRenameFilename}
            >
              <DoneOutlinedIcon fontSize="small" />
            </IconButton>

          </Stack>

        </Box>
        :
        <>
          <Typography variant="text-sm" color={GRAY_900} fontWeight={450} textOverflow="ellipsis" minWidth={0} flex="1 1 auto" overflow="hidden" whiteSpace="nowrap" title={label}>
            {label}
          </Typography>

          <EditOutlinedIcon className="edit-icon" sx={{ color: GRAY_800, fontSize: '1.4rem', display: 'none', cursor: 'pointer' }} />
        </>
      }

    </Stack>
  )
}
